import { useCallback } from "react";
import { formatDateAbbrev } from "@src/packages/utils/useDateFormat";
import { View, Text, Pressable, ScrollView } from "react-native";
import { Bell, CheckCheck, X } from "lucide-react-native";
import Animated, { FadeIn, FadeOut, Layout } from "react-native-reanimated";
import { useNavigation } from "@react-navigation/native";
import {
  useNotifications,
  useMarkNotificationRead,
  useMarkAllNotificationsRead,
} from "@src/packages/care/useNotifications";
import type { Notification } from "@src/packages/care/useNotifications";
import { parseBadgeUnlockEvent } from "@src/packages/badges/badge-events";
import { getNotificationContract } from "@src/packages/notifications/notification-contract";
import { getNotificationRowPresentation } from "@src/packages/notifications/notification-presentation";
import { Dialog, IconButton } from "@louise/moon-ui";
import { EMBER } from "../../../onboarding/theme";
import ThiingsIcon from "../../../shared/ThiingsIcon";
import type { ThiingsIconName } from "../../../shared/icons";
import { useTranslations } from "@src/packages/i18n";
import type { TranslationFunction } from "@src/packages/i18n";
import { getNotificationNavAction } from "@src/packages/notifications/notification-routing";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

type Props = {
  visible: boolean;
  onClose: () => void;
};

function formatRelativeTime(
  t: TranslationFunction,
  timestamp: string,
  dateFmt: (d: Date) => string,
): string {
  const now = new Date();
  const date = new Date(timestamp);
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMs < 0) return t("notifications.time_upcoming");
  if (diffMins < 1) return t("notifications.time_just_now");
  if (diffMins < 60) {
    return t("notifications.time_minutes", {
      params: { minutes: String(diffMins) },
    });
  }
  if (diffHours < 24) {
    return t("notifications.time_hours", {
      params: { hours: String(diffHours) },
    });
  }
  if (diffDays === 1) return t("notifications.time_yesterday");
  if (diffDays < 7) {
    return t("notifications.time_days", {
      params: { days: String(diffDays) },
    });
  }
  return dateFmt(date);
}

// ─── Empty State (no notifications at all) ───────────────────────────────────

function EmptyState() {
  const t = useTranslations();
  return (
    <View
      testID="notification-empty-state"
      style={{
        alignItems: "center",
        paddingVertical: 48,
        paddingHorizontal: 24,
      }}
    >
      <View
        style={{
          width: 64,
          height: 64,
          borderRadius: 32,
          backgroundColor: `${EMBER.colors.warm}10`,
          alignItems: "center",
          justifyContent: "center",
          marginBottom: 16,
        }}
      >
        <Bell size={28} color={EMBER.colors.textFaint} strokeWidth={1.5} />
      </View>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 16,
          fontWeight: "600",
          color: EMBER.colors.text,
          marginBottom: 6,
        }}
      >
        {t("notifications.empty_title")}
      </Text>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 13,
          color: EMBER.colors.textLight,
          textAlign: "center",
          lineHeight: 19,
        }}
      >
        {t("notifications.empty_subtitle")}
      </Text>
    </View>
  );
}

// ─── All Read Banner (compact, shown above past notifications) ───────────────

function AllReadBanner() {
  const t = useTranslations();
  return (
    <View
      testID="notification-all-read-banner"
      style={{
        flexDirection: "row",
        alignItems: "center",
        gap: 10,
        paddingVertical: 14,
        paddingHorizontal: 14,
        marginBottom: 12,
        borderRadius: 12,
        backgroundColor: `${EMBER.colors.green}08`,
        borderWidth: 1,
        borderColor: `${EMBER.colors.green}18`,
      }}
    >
      <View
        style={{
          width: 32,
          height: 32,
          borderRadius: 16,
          backgroundColor: `${EMBER.colors.green}12`,
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CheckCheck size={16} color={EMBER.colors.green} strokeWidth={1.5} />
      </View>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 13,
          fontWeight: "500",
          color: EMBER.colors.textLight,
        }}
      >
        {t("notifications.all_caught_up")}
      </Text>
    </View>
  );
}

// ─── Error State ─────────────────────────────────────────────────────────────

function ErrorState({ onRetry }: { onRetry: () => void }) {
  const t = useTranslations();
  const capturePress = usePressCapture("ErrorState");
  return (
    <View
      testID="notification-error-state"
      style={{
        alignItems: "center",
        paddingVertical: 48,
        paddingHorizontal: 24,
      }}
    >
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 14,
          color: EMBER.colors.textLight,
          textAlign: "center",
          marginBottom: 12,
        }}
      >
        {t("notifications.load_error")}
      </Text>
      <Pressable
        onPress={() => {
          capturePress(
            pressLabel(t("notifications.try_again")),
            "notifications.try_again",
          );
          onRetry();
        }}
        accessibilityRole="button"
        accessibilityLabel={t("notifications.try_again")}
        testID="notification-retry"
        style={{
          paddingHorizontal: 20,
          paddingVertical: 8,
          borderRadius: 8,
          backgroundColor: `${EMBER.colors.warm}14`,
        }}
      >
        <Text
          className="font-dm-sans"
          style={{ fontSize: 13, fontWeight: "500", color: EMBER.colors.warm }}
        >
          {t("notifications.try_again")}
        </Text>
      </Pressable>
    </View>
  );
}

// ─── Loading State ───────────────────────────────────────────────────────────

function LoadingSkeleton() {
  return (
    <View
      testID="notification-loading-state"
      style={{ paddingVertical: 16, gap: 12 }}
    >
      {[1, 2, 3].map((i) => (
        <View
          key={i}
          style={{
            flexDirection: "row",
            padding: 14,
            gap: 14,
            borderRadius: 12,
          }}
        >
          <View
            style={{
              width: 40,
              height: 40,
              borderRadius: 10,
              backgroundColor: "rgba(0,0,0,0.04)",
            }}
          />
          <View style={{ flex: 1, gap: 8 }}>
            <View
              style={{
                width: "60%",
                height: 14,
                borderRadius: 4,
                backgroundColor: "rgba(0,0,0,0.04)",
              }}
            />
            <View
              style={{
                width: "90%",
                height: 12,
                borderRadius: 4,
                backgroundColor: "rgba(0,0,0,0.03)",
              }}
            />
          </View>
        </View>
      ))}
    </View>
  );
}

// ─── Notification Row ────────────────────────────────────────────────────────

function NotificationRow({
  notification,
  onPress,
}: {
  notification: Notification;
  onPress: (n: Notification) => void;
}) {
  const t = useTranslations();
  const capturePress = usePressCapture("NotificationRow");
  const contract = getNotificationContract(notification.type);
  const presentation = getNotificationRowPresentation(notification);
  const iconName = contract.icon as ThiingsIconName;
  const isRead = !!notification.readAt;
  const badgeEvent = parseBadgeUnlockEvent(notification);
  const quietBadgeEvent = badgeEvent?.event.copy.celebrationMode === "quiet";
  const title = quietBadgeEvent
    ? t("notifications.badge_ready_title")
    : (badgeEvent?.event.copy.title ?? presentation.title);
  const body = quietBadgeEvent
    ? t("notifications.badge_ready_body")
    : (badgeEvent?.event.copy.body ?? presentation.body);
  const metadata = quietBadgeEvent ? [] : presentation.metadata;

  return (
    <Animated.View
      testID={`notification-row-${notification.id}`}
      entering={FadeIn.duration(200)}
      exiting={FadeOut.duration(150)}
      layout={Layout.springify()}
    >
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          marginBottom: 4,
          opacity: isRead ? 0.55 : 1,
          borderRadius: 12,
        }}
      >
        <Pressable
          onPress={() => {
            capturePress(pressLabel(title), "notification_row");
            onPress(notification);
          }}
          accessibilityRole="button"
          accessibilityLabel={title}
          testID={`notification-open-${notification.id}`}
          style={({ pressed }) => ({
            flex: 1,
            flexDirection: "row",
            padding: 14,
            gap: 12,
            backgroundColor: pressed ? "rgba(0,0,0,0.03)" : "transparent",
            borderRadius: 12,
          })}
        >
          {/* Icon */}
          <ThiingsIcon name={iconName} size={28} />

          {/* Content */}
          <View style={{ flex: 1 }}>
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                gap: 6,
                marginBottom: 2,
              }}
            >
              {!isRead && (
                <View
                  testID={`notification-unread-indicator-${notification.id}`}
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: 3,
                    backgroundColor: EMBER.colors.warm,
                  }}
                />
              )}
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 14,
                  fontWeight: isRead ? "400" : "600",
                  color: EMBER.colors.text,
                  flex: 1,
                }}
                numberOfLines={2}
              >
                {title}
              </Text>
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 11,
                  color: EMBER.colors.textFaint,
                  marginLeft: 4,
                }}
              >
                {formatRelativeTime(
                  t,
                  notification.createdAt,
                  formatDateAbbrev,
                )}
              </Text>
            </View>
            {body ? (
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 13,
                  color: EMBER.colors.textLight,
                  marginTop: 2,
                  lineHeight: 18,
                }}
                numberOfLines={2}
              >
                {body}
              </Text>
            ) : null}
            {metadata.length > 0 ? (
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 11,
                  color: EMBER.colors.textFaint,
                  marginTop: 4,
                }}
                numberOfLines={1}
              >
                {metadata.join(" · ")}
              </Text>
            ) : null}
          </View>
        </Pressable>
      </View>
    </Animated.View>
  );
}

// ─── Main Sheet ──────────────────────────────────────────────────────────────

function NotificationContent({ onClose }: { onClose: () => void }) {
  const t = useTranslations();
  const navigation = useNavigation();
  const {
    data: notifications = [],
    isLoading,
    isError,
    refetch,
  } = useNotifications({ enabled: true });
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();
  const capturePress = usePressCapture("NotificationContent");

  const unreadCount = notifications.filter((n) => !n.readAt).length;

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleNotificationPress = useCallback(
    (notification: Notification) => {
      // Navigate if the notification type supports it
      const navAction = getNotificationNavAction(notification);
      if (navAction) {
        handleClose();
        // Small delay so close animation plays before routing.
        setTimeout(() => {
          navigation.dispatch(navAction);
          if (!notification.readAt) {
            markRead.mutate(notification.id);
          }
        }, 250);
      }
    },
    [markRead, navigation, handleClose],
  );

  const handleMarkAllRead = useCallback(() => {
    capturePress(
      pressLabel(t("notifications.mark_all_read")),
      "notifications.mark_all_read",
    );
    if (unreadCount > 0) {
      markAllRead.mutate();
    }
  }, [capturePress, markAllRead, t, unreadCount]);

  return (
    <View style={{ flex: 1, minHeight: 0 }}>
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          gap: 10,
          marginBottom: 16,
          paddingRight: 2,
        }}
      >
        <Text
          testID="notification-center-title"
          accessibilityRole="header"
          className="font-dm-sans"
          style={{
            fontSize: 20,
            fontWeight: "700",
            color: EMBER.colors.text,
          }}
          numberOfLines={1}
        >
          {t("notifications.title")}
        </Text>

        <View style={{ flex: 1 }} />

        <IconButton
          onPress={handleClose}
          hitSlop={12}
          accessibilityLabel="Close"
          size={32}
          surface="soft"
          backgroundColor="rgba(0,0,0,0.06)"
          pressedBackgroundColor="rgba(0,0,0,0.06)"
          pressedOpacity={1}
          testID="notification-center-close"
        >
          <X size={16} color={EMBER.colors.textFaint} />
        </IconButton>
      </View>

      {unreadCount > 0 && (
        <Pressable
          onPress={handleMarkAllRead}
          accessibilityRole="button"
          accessibilityLabel={t("notifications.mark_all_read")}
          testID="notification-mark-all-read"
          disabled={markAllRead.isPending}
          hitSlop={4}
          style={{
            flexDirection: "row",
            alignItems: "center",
            gap: 5,
            alignSelf: "flex-end",
            marginBottom: 10,
            paddingVertical: 4,
            opacity: markAllRead.isPending ? 0.5 : 1,
          }}
        >
          <CheckCheck
            size={14}
            color={EMBER.colors.textLight}
            strokeWidth={1.5}
          />
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 12,
              fontWeight: "600",
              color: EMBER.colors.textLight,
            }}
          >
            {t("notifications.mark_all_read")}
          </Text>
        </Pressable>
      )}

      <View style={{ flex: 1, minHeight: 0 }}>
        {isLoading ? (
          <ScrollView showsVerticalScrollIndicator={false}>
            <LoadingSkeleton />
          </ScrollView>
        ) : isError ? (
          <View style={{ flex: 1, justifyContent: "center" }}>
            <ErrorState onRetry={() => refetch()} />
          </View>
        ) : notifications.length === 0 ? (
          <View style={{ flex: 1, justifyContent: "center" }}>
            <EmptyState />
          </View>
        ) : (
          <ScrollView
            showsVerticalScrollIndicator={false}
            style={{ flex: 1 }}
            contentContainerStyle={{ paddingBottom: 4 }}
          >
            <View
              style={{
                gap: 2,
              }}
            >
              {unreadCount === 0 && <AllReadBanner />}
              {notifications.map((notification) => (
                <NotificationRow
                  key={notification.id}
                  notification={notification}
                  onPress={handleNotificationPress}
                />
              ))}
            </View>
          </ScrollView>
        )}
      </View>
    </View>
  );
}

export default function NotificationCenterSheet({ visible, onClose }: Props) {
  if (!visible) return null;

  return (
    <Dialog
      visible={visible}
      onClose={onClose}
      layout="centered"
      closeButton={false}
      containerStyle={{
        width: "86%",
        maxWidth: 390,
        minHeight: 520,
        height: "74%",
        maxHeight: 680,
        borderRadius: 28,
      }}
      surfaceStyle={{
        height: "100%",
        borderRadius: 28,
        overflow: "hidden",
      }}
      contentStyle={{
        flex: 1,
        paddingTop: 18,
        paddingHorizontal: 18,
        paddingBottom: 18,
      }}
      testID="notification-center-dialog"
    >
      <NotificationContent onClose={onClose} />
    </Dialog>
  );
}
